import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, getDoc, updateDoc, deleteDoc, setDoc } from 'firebase/firestore';
import { db, auth } from './firebase';

const StaffManager = ({ tenantId }) => {
  const [staff, setStaff] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Inline Editing State
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ role: '', schoolId: '', status: '', assignedCaseManager: '' });

  // Whitelisting State
  const [newStaffName, setNewStaffName] = useState('');
  const [newStaffEmail, setNewStaffEmail] = useState('');
  const [newStaffRole, setNewStaffRole] = useState('');
  const [newStaffSchool, setNewStaffSchool] = useState('');
  const [newStaffCM, setNewStaffCM] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const fetchData = async () => {
    try {
      const staffRef = collection(db, 'districts', tenantId, 'users');
      const staffSnap = await getDocs(staffRef);
      const staffData = staffSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setStaff(staffData);

      const schoolRef = doc(db, 'districts', tenantId, 'settings', 'schools');
      const schoolSnap = await getDoc(schoolRef);
      if (schoolSnap.exists() && schoolSnap.data().schoolList) {
        setSchools(schoolSnap.data().schoolList);
      }
    } catch (error) {
      console.error("Error fetching staff or schools:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [tenantId]);

  const caseManagers = staff.filter(s => s.role === 'Case Manager');

  // --- EXISTING LOGIC: EDIT & DELETE ---
  const handleEditClick = (user) => {
    setEditingId(user.id);
    setEditForm({
      role: user.role || '',
      schoolId: user.schoolId || '',
      status: user.status || 'Active',
      assignedCaseManager: user.assignedCaseManager || ''
    });
  };

  const handleCancelEdit = () => setEditingId(null);

  const handleSaveEdit = async (userId) => {
    try {
      const userRef = doc(db, 'districts', tenantId, 'users', userId);
      await updateDoc(userRef, {
        role: editForm.role,
        schoolId: editForm.schoolId,
        status: editForm.status,
        assignedCaseManager: editForm.role === 'Instructional Assistant' ? editForm.assignedCaseManager : ''
      });
      setStaff(prev => prev.map(user => user.id === userId ? { ...user, ...editForm, assignedCaseManager: editForm.role === 'Instructional Assistant' ? editForm.assignedCaseManager : '' } : user));
      setEditingId(null);
    } catch (error) {
      console.error("Error updating user:", error);
      alert("Failed to update user. Check permissions.");
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`WARNING: Are you sure you want to permanently delete ${userName}? They will lose all access.`)) return;
    try {
      await deleteDoc(doc(db, 'districts', tenantId, 'users', userId));
      setStaff(prev => prev.filter(user => user.id !== userId));
    } catch (error) {
      console.error("Error deleting user:", error);
      alert("Failed to delete user.");
    }
  };

  // --- NEW LOGIC: WHITELIST NEW STAFF ---
  const handleAddStaff = async (e) => {
    e.preventDefault();
    if (!newStaffName || !newStaffEmail || !newStaffRole) return;

    setIsAdding(true);
    const cleanEmail = newStaffEmail.toLowerCase().trim();

    try {
      const newUser = {
        name: newStaffName.trim(), 
        email: cleanEmail,
        role: newStaffRole,
        schoolId: newStaffSchool || '',
        assignedCaseManager: newStaffRole === 'Instructional Assistant' ? newStaffCM : '',
        status: 'Pending Registration'
      };

      await setDoc(doc(db, 'districts', tenantId, 'users', cleanEmail), newUser);
      setStaff(prev => [...prev, { id: cleanEmail, ...newUser }]);
      
      setNewStaffName('');
      setNewStaffEmail('');
      setNewStaffRole('');
      setNewStaffSchool('');
      setNewStaffCM('');
    } catch (error) {
      console.error("Error whitelisting staff:", error);
      alert("Failed to whitelist new staff member.");
    } finally {
      setIsAdding(false);
    }
  };

  if (loading) return <div>Loading personnel data...</div>;

  return (
    <div style={{ backgroundColor: '#fff', padding: '25px', borderRadius: '8px', border: '1px solid #ddd' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h3 style={{ margin: '0 0 5px 0', color: '#003366', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>👥</span> Personnel & Role Management
          </h3>
          <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>
            Manage existing staff or whitelist new users for building access.
          </p>
        </div>
      </div>

      <div style={{ overflowX: 'auto', marginBottom: '30px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
          <thead>
            <tr style={{ backgroundColor: '#f4f7f6', borderBottom: '2px solid #ddd' }}>
              <th style={{ padding: '12px', color: '#333' }}>Staff Member</th>
              <th style={{ padding: '12px', color: '#333' }}>System Role</th>
              <th style={{ padding: '12px', color: '#333' }}>Building Assignment</th>
              <th style={{ padding: '12px', color: '#333' }}>Account Status</th>
              <th style={{ padding: '12px', color: '#333', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {staff.map(user => {
              const isEditing = editingId === user.id;

              return (
                <tr key={user.id} style={{ borderBottom: '1px solid #eee', backgroundColor: isEditing ? '#fcfcfc' : '#fff' }}>
                  <td style={{ padding: '12px' }}>
                    <div style={{ fontWeight: 'bold', color: '#003366' }}>{user.name || 'Pending Claim'}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>{user.email || user.id}</div>
                  </td>
                  <td style={{ padding: '12px' }}>
                    {isEditing ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <select value={editForm.role} onChange={(e) => setEditForm({...editForm, role: e.target.value})} style={{ padding: '6px', borderRadius: '4px', border: '1px solid #ccc', width: '100%' }}>
                          <option value="">-- Select Role --</option>
                          <option value="Gen Ed">Gen Ed</option>
                          <option value="Case Manager">Case Manager</option>
                          <option value="ECE Recorder">ECE Recorder</option>
                          <option value="Instructional Assistant">Instructional Assistant</option>
                          <option value="District Admin">District Admin</option>
                          <option value="SuperAdmin">SuperAdmin</option>
                        </select>
                        {editForm.role === 'Instructional Assistant' && (
                          <select value={editForm.assignedCaseManager} onChange={(e) => setEditForm({...editForm, assignedCaseManager: e.target.value})} style={{ padding: '6px', borderRadius: '4px', border: '1px solid #003366', backgroundColor: '#e6f0fa', width: '100%', fontSize: '12px' }}>
                            <option value="">-- Assign to Case Manager --</option>
                            {caseManagers.map(cm => <option key={cm.id} value={cm.id}>{cm.name || cm.email}</option>)}
                          </select>
                        )}
                      </div>
                    ) : (
                      <div>
                        <span style={{ padding: '4px 8px', backgroundColor: '#e6f0fa', color: '#003366', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', display: 'inline-block' }}>{user.role || 'Unassigned'}</span>
                        {user.role === 'Instructional Assistant' && (
                          <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                            Reports to: {caseManagers.find(cm => cm.id === user.assignedCaseManager)?.name || 'Unassigned'}
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '12px' }}>
                    {isEditing ? (
                      <select value={editForm.schoolId} onChange={(e) => setEditForm({...editForm, schoolId: e.target.value})} style={{ padding: '6px', borderRadius: '4px', border: '1px solid #ccc', width: '100%' }}>
                        <option value="">-- Select Building --</option>
                        <option value="District Office">District Office</option>
                        {schools.map((school, idx) => (
                          <option key={idx} value={school}>{school}</option>
                        ))}
                      </select>
                    ) : (
                      <span style={{ color: '#555', fontSize: '14px' }}>{user.schoolId || 'None'}</span>
                    )}
                  </td>
                  <td style={{ padding: '12px' }}>
                    {isEditing ? (
                      <select value={editForm.status} onChange={(e) => setEditForm({...editForm, status: e.target.value})} style={{ padding: '6px', borderRadius: '4px', border: '1px solid #ccc', width: '100%' }}>
                        <option value="Active">Active</option>
                        <option value="Suspended">Suspended</option>
                        <option value="Pending Registration">Pending Registration</option>
                      </select>
                    ) : (
                      <span style={{ padding: '4px 8px', backgroundColor: user.status === 'Active' ? '#e6ffe6' : (user.status === 'Suspended' ? '#ffe6e6' : '#fff3e6'), color: user.status === 'Active' ? '#006600' : (user.status === 'Suspended' ? '#ba0c2f' : '#cc7a00'), borderRadius: '4px', fontSize: '12px', fontWeight: 'bold' }}>{user.status || 'Unknown'}</span>
                    )}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right' }}>
                    {isEditing ? (
                      <div style={{ display: 'flex', gap: '5px', justifyContent: 'flex-end' }}>
                        <button onClick={() => handleSaveEdit(user.id)} style={{ padding: '6px 12px', backgroundColor: '#28a745', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Save</button>
                        <button onClick={handleCancelEdit} style={{ padding: '6px 12px', backgroundColor: '#6c757d', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Cancel</button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: '5px', justifyContent: 'flex-end' }}>
                        <button onClick={() => handleEditClick(user)} style={{ padding: '6px 12px', backgroundColor: '#fff', color: '#003366', border: '1px solid #003366', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Edit</button>
                        {user.id !== auth?.currentUser?.uid && (
                           <button onClick={() => handleDeleteUser(user.id, user.name || user.email)} style={{ padding: '6px 12px', backgroundColor: '#fff', color: '#ba0c2f', border: '1px solid #ba0c2f', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Delete</button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* THE WHITELISTING ENGINE */}
      <div style={{ backgroundColor: '#f9f9f9', padding: '20px', borderRadius: '6px', border: '1px solid #eee' }}>
        <h4 style={{ margin: '0 0 15px 0', color: '#333' }}>+ Whitelist New Staff Member</h4>
        <form onSubmit={handleAddStaff} style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          
          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#666', marginBottom: '5px' }}>Full Name</label>
            <input 
              type="text" 
              value={newStaffName} 
              onChange={(e) => setNewStaffName(e.target.value)} 
              placeholder="e.g. Jane Educator"
              style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box' }}
              required 
            />
          </div>

          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#666', marginBottom: '5px' }}>Email Address</label>
            <input 
              type="email" 
              value={newStaffEmail} 
              onChange={(e) => setNewStaffEmail(e.target.value)} 
              placeholder="teacher@district.org"
              style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box' }}
              required 
            />
          </div>

          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#666', marginBottom: '5px' }}>System Role</label>
            <select 
              value={newStaffRole} 
              onChange={(e) => setNewStaffRole(e.target.value)} 
              style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box' }}
              required
            >
              <option value="">-- Select Role --</option>
              <option value="Gen Ed">Gen Ed</option>
              <option value="Case Manager">Case Manager</option>
              <option value="ECE Recorder">ECE Recorder</option>
              <option value="Instructional Assistant">Instructional Assistant</option>
              <option value="District Admin">District Admin</option>
              <option value="SuperAdmin">SuperAdmin</option>
            </select>
          </div>

          {newStaffRole === 'Instructional Assistant' && (
            <div style={{ flex: '1 1 200px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#003366', marginBottom: '5px' }}>Assign to Case Manager</label>
              <select 
                value={newStaffCM} 
                onChange={(e) => setNewStaffCM(e.target.value)} 
                style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #003366', backgroundColor: '#e6f0fa', boxSizing: 'border-box' }}
                required
              >
                <option value="">-- Select Manager --</option>
                {caseManagers.map(cm => <option key={cm.id} value={cm.id}>{cm.name || cm.email}</option>)}
              </select>
            </div>
          )}

          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#666', marginBottom: '5px' }}>Building Assignment</label>
            <select 
              value={newStaffSchool} 
              onChange={(e) => setNewStaffSchool(e.target.value)} 
              style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box' }}
            >
              <option value="">-- Optional / Unassigned --</option>
              <option value="District Office">District Office</option>
              {schools.map((school, idx) => (
                <option key={idx} value={school}>{school}</option>
              ))}
            </select>
          </div>

          <button 
            type="submit" 
            disabled={isAdding}
            style={{ 
              padding: '10px 20px', 
              backgroundColor: '#003366', 
              color: '#fff', 
              border: 'none', 
              borderRadius: '4px', 
              fontWeight: 'bold', 
              cursor: isAdding ? 'not-allowed' : 'pointer',
              height: '40px'
            }}
          >
            {isAdding ? 'Adding...' : 'Add to Whitelist'}
          </button>
        </form>
      </div>

    </div>
  );
};

export default StaffManager;